import { useState, useCallback, useRef, useEffect } from 'react';
import { userFileService } from '../services/fileService';
import { t } from '../i18n';

/**
 * 异步任务状态管理 Hook
 * 用于管理压缩、解压、远程下载等异步任务的轮询和进度显示
 */
const useAsyncTask = (containerId) => {
    const [tasks, setTasks] = useState([]);
    const [activeTasks, setActiveTasks] = useState({});
    const pollIntervalRef = useRef(null);
    const pollTimeoutRef = useRef({});

    // 添加任务
    const addTask = useCallback((taskId, type, description) => {
        const task = {
            id: taskId,
            type,
            description,
            status: 'pending',
            progress: 0,
            message: '',
            createdAt: new Date().toISOString()
        };
        setTasks(prev => [task, ...prev]);
        setActiveTasks(prev => ({ ...prev, [taskId]: task }));
        return taskId;
    }, []);

    // 更新任务状态
    const updateTask = useCallback((taskId, updates) => {
        setTasks(prev => prev.map(task =>
            task.id === taskId ? { ...task, ...updates } : task
        ));
        setActiveTasks(prev => {
            if (prev[taskId]) {
                const updated = { ...prev[taskId], ...updates };
                if (updates.status === 'completed' || updates.status === 'failed') {
                    const { [taskId]: removed, ...rest } = prev;
                    return rest;
                }
                return { ...prev, [taskId]: updated };
            }
            return prev;
        });
    }, []);

    // 轮询任务状态
    const pollTaskStatus = useCallback(async (taskId, onComplete, onError) => {
        if (!containerId || !taskId) return;

        const poll = async () => {
            try {
                const response = await userFileService.getTaskStatus(containerId, taskId);
                if (response.success && response.data) {
                    const taskData = response.data;
                    updateTask(taskId, {
                        status: taskData.status,
                        progress: taskData.progress || 0,
                        message: taskData.message || '',
                        output: taskData.output
                    });

                    if (taskData.status === 'completed') {
                        onComplete && onComplete(taskData);
                        return;
                    } else if (taskData.status === 'failed') {
                        onError && onError(taskData.message || 'Task failed');
                        return;
                    }

                    // 继续轮询
                    pollTimeoutRef.current[taskId] = setTimeout(poll, 1000);
                } else {
                    onError && onError(response.error || 'Failed to get task status');
                }
            } catch (error) {
                onError && onError(error.message);
            }
        };

        poll();
    }, [containerId, updateTask]);

    // 停止轮询
    const stopPolling = useCallback((taskId) => {
        if (pollTimeoutRef.current[taskId]) {
            clearTimeout(pollTimeoutRef.current[taskId]);
            delete pollTimeoutRef.current[taskId];
        }
    }, []);

    // 执行异步压缩
    const compressAsync = useCallback(async (srcPath, dstPath, format = 'tar.gz') => {
        const response = await userFileService.compressFileAsync(containerId, srcPath, dstPath, format);
        if (response.success && response.data?.task_id) {
            const taskId = response.data.task_id;
            addTask(taskId, 'compress', t('Compress: {{name}}', { name: srcPath.split('/').pop() }));
            return { success: true, taskId };
        }
        return { success: false, error: response.error || 'Failed to start compress task' };
    }, [containerId, addTask]);

    // 执行异步解压
    const extractAsync = useCallback(async (srcPath, dstPath) => {
        const response = await userFileService.extractFileAsync(containerId, srcPath, dstPath);
        if (response.success && response.data?.task_id) {
            const taskId = response.data.task_id;
            addTask(taskId, 'extract', t('Extract: {{name}}', { name: srcPath.split('/').pop() }));
            return { success: true, taskId };
        }
        return { success: false, error: response.error || 'Failed to start extract task' };
    }, [containerId, addTask]);

    // 执行远程下载
    const remoteDownloadAsync = useCallback(async (url, dstPath) => {
        const response = await userFileService.remoteDownload(containerId, url, dstPath, true);
        if (response.success && response.data?.task_id) {
            const taskId = response.data.task_id;
            addTask(taskId, 'download', t('Download: {{name}}', { name: url.split('/').pop() }));
            return { success: true, taskId };
        }
        return { success: false, error: response.error || 'Failed to start download task' };
    }, [containerId, addTask]);

    // 清理完成的任务
    const clearCompletedTasks = useCallback(() => {
        setTasks(prev => prev.filter(task =>
            task.status !== 'completed' && task.status !== 'failed'
        ));
    }, []);

    // 组件卸载时清理所有轮询
    useEffect(() => {
        return () => {
            Object.keys(pollTimeoutRef.current).forEach(taskId => {
                clearTimeout(pollTimeoutRef.current[taskId]);
            });
        };
    }, []);

    return {
        tasks,
        activeTasks,
        hasActiveTasks: Object.keys(activeTasks).length > 0,
        addTask,
        updateTask,
        pollTaskStatus,
        stopPolling,
        compressAsync,
        extractAsync,
        remoteDownloadAsync,
        clearCompletedTasks
    };
};

export default useAsyncTask;
